// The record's behaviour, once — so the two surfaces cannot disagree about what
// a button does, only about where it sits.
//
// It owns the record query, the three overlays (start a visit, edit the
// patient, delete the patient) and every navigation the record can make. Both
// `PatientRecord.web.jsx` and `PatientRecord.mobile.jsx` render its return
// value; neither one holds data state of its own.

import React from 'react';
import { useLocation } from 'react-router-dom';
import { useToast } from '@parsina/framework';

import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useCaseRecord } from '../../hooks/useCaseRecord.js';
import { caseToForm, buildCaseUpdate } from '../../lib/caseForm.js';
import { errorMessageKey } from '../../lib/bffClient.js';
import { defaultVisitReason } from './PatientRecord.shared.js';

export function usePatientRecordScreen(caseId) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useAppNavigate();
  const location = useLocation();

  const record = useCaseRecord(caseId);
  const { caseRow } = record;

  // ── Start a visit ───────────────────────────────────────────────────────
  const [startOpen, setStartOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [starting, setStarting] = React.useState(false);

  const openStart = React.useCallback(() => {
    setReason(defaultVisitReason(caseRow));
    setStartOpen(true);
  }, [caseRow]);

  /**
   * The list's «شروع ویزیت» hands the intent over in ROUTER STATE rather than
   * in the URL: `?start=1` would be a link that re-raises the dialog every time
   * it is pasted, bookmarked or refreshed. It fires once, when the record has
   * loaded enough to pre-fill the reason.
   */
  const consumed = React.useRef(false);
  React.useEffect(() => {
    if (consumed.current || !caseRow) return;
    if (!location.state?.startVisit) return;
    consumed.current = true;
    if (caseRow.openEncounterId) {
      navigate('visit', { caseId, encounterId: caseRow.openEncounterId, replace: true });
      return;
    }
    openStart();
  }, [caseRow, location.state, openStart, navigate, caseId]);

  /**
   * Start, or go to the visit that was already open.
   *
   * `ENCOUNTER_ALREADY_OPEN` is not an error the student can act on — there IS
   * a visit for this patient and they asked to be in one — so the hook resolves
   * it to a destination and this toasts on the way (core.md §7.4).
   */
  const submitStart = React.useCallback(async () => {
    setStarting(true);
    try {
      const { encounter, alreadyOpenId } = await record.startEncounter(reason);
      setStartOpen(false);
      if (alreadyOpenId) {
        toast.info(t('record.startVisit.alreadyOpen'));
        navigate('visit', { caseId, encounterId: alreadyOpenId });
        return;
      }
      if (encounter?.id) navigate('visit', { caseId, encounterId: encounter.id });
    } catch (err) {
      toast.error(t(errorMessageKey(err)));
    } finally {
      setStarting(false);
    }
  }, [record, reason, navigate, caseId, t, toast]);

  const startVisit = React.useCallback(() => {
    // Same button, two jobs (core.md §7.4): continue the open visit, or open the
    // dialog that starts one.
    if (caseRow?.openEncounterId) {
      navigate('visit', { caseId, encounterId: caseRow.openEncounterId });
      return;
    }
    openStart();
  }, [caseRow, navigate, caseId, openStart]);

  // ── Edit the patient ────────────────────────────────────────────────────
  const [editOpen, setEditOpen] = React.useState(false);
  const [form, setForm] = React.useState(() => caseToForm(null));
  const [savingEdit, setSavingEdit] = React.useState(false);
  const [showTitleError, setShowTitleError] = React.useState(false);

  const openEdit = React.useCallback(() => {
    setForm(caseToForm(caseRow));
    setShowTitleError(false);
    setEditOpen(true);
  }, [caseRow]);

  const submitEdit = React.useCallback(async () => {
    if (!String(form.title || '').trim()) {
      setShowTitleError(true);
      return;
    }
    // ONLY WHAT CHANGED. Sending the whole form would work exactly as long as
    // the form holds every field — and the day it holds a subset, editing the
    // title erases the rest.
    const patch = buildCaseUpdate(caseRow, form);
    if (!Object.keys(patch).length) {
      setEditOpen(false);
      return;
    }
    setSavingEdit(true);
    try {
      await record.updateCase(patch);
      setEditOpen(false);
      toast.success(t('record.saved'));
    } catch (err) {
      toast.error(t(errorMessageKey(err)));
    } finally {
      setSavingEdit(false);
    }
  }, [form, caseRow, record, t, toast]);

  // ── Archive / delete ────────────────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const toggleArchive = React.useCallback(async () => {
    const next = caseRow?.status === 'archived' ? 'active' : 'archived';
    try {
      await record.updateCase({ status: next });
      toast.success(t(next === 'archived' ? 'patients.archived' : 'patients.unarchived'));
    } catch (err) {
      toast.error(t(errorMessageKey(err)));
    }
  }, [caseRow, record, t, toast]);

  const confirmDelete = React.useCallback(async () => {
    setDeleting(true);
    try {
      await record.deleteCase();
      toast.success(t('patients.deleted'));
      // Back to the list, REPLACING this entry: the record the student just
      // deleted must not be one step back on the history stack.
      navigate('patients', { replace: true });
    } catch (err) {
      toast.error(t(errorMessageKey(err)));
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  }, [record, navigate, t, toast]);

  // ── Navigation ──────────────────────────────────────────────────────────
  const openVisit = React.useCallback(
    (encounter) => navigate('visit', { caseId, encounterId: encounter.id }),
    [navigate, caseId],
  );
  const openChat = React.useCallback(
    (thread) => navigate('chat', { caseId, threadId: thread?.id }),
    [navigate, caseId],
  );
  const back = React.useCallback(() => navigate('patients'), [navigate]);

  return {
    ...record,
    archived: caseRow?.status === 'archived',
    // start a visit
    startOpen,
    startVisit,
    closeStart: () => setStartOpen(false),
    reason,
    setReason,
    starting,
    submitStart,
    // edit
    editOpen,
    openEdit,
    closeEdit: () => setEditOpen(false),
    form,
    setForm: (patch) => setForm((prev) => ({ ...prev, ...patch })),
    savingEdit,
    showTitleError,
    submitEdit,
    // archive / delete
    toggleArchive,
    deleteOpen,
    openDelete: () => setDeleteOpen(true),
    closeDelete: () => setDeleteOpen(false),
    deleting,
    confirmDelete,
    // navigation
    openVisit,
    openChat,
    back,
    navigate,
  };
}

export default usePatientRecordScreen;
